import { Icon } from '@aqarmap/aqarmap-ui';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Languages } from '~/constants/enums';
import { useGetTopSellersQuery } from '~/redux/api/appApiSlice';
import RequestCallButton from '~/pages/LeadFollowUp/RequestCallButton';
import TopSellerCompanyCard from '~/pages/LeadFollowUp/TopSellerCompanyCard';
import TopSellersLoader from '~/pages/LeadFollowUp/TopSellersLoader';
import { topSellersSliderBreakPoints } from '~/pages/LeadFollowUp/utils';
import useSwiperNavigation from '~valuation/hooks/useSwiperNavigation';

export default function TopSellersSection({
  leadId,
  propertyType,
  location,
  section,
  hash,
}: {
  leadId: string;
  propertyType: { id: number };
  location: { id: number };
  section: { id: number };
  hash: string;
}) {
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const isRtl = locale === Languages.ARABIC;
  const {
    handleInit,
    handleNext,
    handlePrev,
    handleSlideChange,
    isEnd,
    isStart,
    sliderRef,
  } = useSwiperNavigation();
  const { data: topSellers, isLoading } = useGetTopSellersQuery({
    locale,
    location: location.id,
    propertyType: propertyType.id,
    section: section.id,
  });

  if (isLoading) return <TopSellersLoader />;
  if (!topSellers?.length) return null;

  const scrollBtnStyles =
    'element-center hidden h-[32px] w-[32px] rounded-full bg-white md:flex';

  return (
    <section className="flex w-full flex-col gap-2x rounded-[16px] bg-blue_light_2 p-2x md:gap-3x md:p-3x">
      <div className="flex items-center justify-between">
        <p className="text-title_4 text-black">
          {t('leadFollowUpTopSellersTitle')}
        </p>
        <div className="flex gap-x">
          {!isStart && (
            <button onClick={handlePrev} className={scrollBtnStyles}>
              <Icon
                name={isRtl ? 'chevron-right' : 'chevron-left'}
                size="12px"
                className="text-gray__dark_1"
              />
            </button>
          )}
          {!isEnd && (
            <button onClick={handleNext} className={scrollBtnStyles}>
              <Icon
                name={isRtl ? 'chevron-left' : 'chevron-right'}
                size="12px"
                className="text-gray__dark_1"
              />
            </button>
          )}
        </div>
      </div>
      <Swiper
        ref={sliderRef}
        onInit={handleInit}
        onSlideChange={handleSlideChange}
        spaceBetween={16}
        breakpoints={topSellersSliderBreakPoints}
        mousewheel={{ forceToAxis: true, releaseOnEdges: false }}
        touchStartPreventDefault={false}
        passiveListeners={false}
        className="!w-full !h-[176px]"
      >
        {topSellers.map((topSeller) => (
          <SwiperSlide key={topSeller.id}>
            <TopSellerCompanyCard topSeller={topSeller} />
          </SwiperSlide>
        ))}
      </Swiper>
      <RequestCallButton
        leadId={leadId}
        hash={hash}
        sellers={topSellers.map((topSeller) => topSeller.id)}
      />
    </section>
  );
}
